import React from 'react';
export const ScienceSection = () => {
  return <section className="mb-10" data-id="element-133">
      <div className="bg-blue-50 p-6 border border-blue-100 rounded-lg mb-8" data-id="element-134">
        <h2 className="text-3xl font-bold text-blue-800 mb-4" data-id="element-135">
          The shocking truth about what's really happening to your body every
          night
        </h2>
        <div className="flex flex-col md:flex-row gap-6 mb-6" data-id="element-136">
          <div className="md:w-1/2" data-id="element-137">
            <p className="mb-4 text-gray-700 text-lg" data-id="element-138">
              During my research, I came across Dr. Sarah Mitchell, a
              biomechanical engineer who spent years studying what she calls the
              "Nocturnal Misalignment Cascade" – and her explanation left me
              stunned.
            </p>
            <p className="mb-4 text-gray-700 text-lg" data-id="element-139">
              "Most people think their hip and back pain is just 'bad luck' or
              'getting older,'" she told me during our consultation. "But what's
              actually happening is a precise mechanical problem that occurs
              every single night while you sleep."
            </p>
          </div>
          <div className="md:w-1/2" data-id="element-140">
            <video src="https://cdn.shopify.com/videos/c/o/v/92f725e9e47b41258d4bd5062ab693e2.mp4" autoPlay loop muted playsInline className="rounded-lg w-full h-64 object-cover" data-id="element-141" />
          </div>
        </div>
        <h3 className="text-2xl font-bold text-blue-800 mb-3" data-id="element-142">
          Here's the shocking truth she revealed:
        </h3>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-143">
          For the past 7,000 years, humans have been trying to solve the problem
          of comfortable sleep. Ancient Mesopotamians used stone blocks.
          Egyptians carved wooden headrests. The Chinese believed porcelain
          pillows improved health.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-144">
          But none of them understood the biomechanics of what happens when we
          sleep on our side.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-145">
          When you lie on your side without proper support, your upper leg drops
          down due to gravity. This seemingly innocent movement triggers a
          devastating cascade:
        </p>
        <div className="bg-white p-4 rounded-lg border border-blue-200 mb-4" data-id="element-146">
          <p className="text-blue-800 font-medium text-lg" data-id="element-147">
            → Your pelvis tilts out of alignment → Your spine twists unnaturally
            → Pressure builds on your sciatic nerve → Your hip joint compresses
            against the mattress → Inflammation increases in surrounding tissues
          </p>
        </div>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-148">
          This isn't just uncomfortable – it's literally damaging your body for
          6-8 hours every single night.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-149">
          "Think of it like this," Dr. Mitchell explained. "If you twisted your
          spine and held that position for eight hours during the day, you'd be
          in agony. But because it happens while you sleep, you wake up thinking
          the pain is mysterious or inevitable."
        </p>
      </div>
      <div className="bg-red-50 p-6 border border-red-100 rounded-lg mb-8" data-id="element-150">
        <h2 className="text-3xl font-bold text-red-800 mb-4" data-id="element-151">
          The vicious cycle that's slowly destroying your quality of life
        </h2>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-152">
          But here's where it gets even worse.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-153">
          This nightly misalignment doesn't just cause morning pain – it creates
          what researchers call a "bidirectional feedback loop" that gets more
          devastating with each passing night:
        </p>
        <div className="bg-white p-4 rounded-lg border border-red-200 mb-4" data-id="element-154">
          <p className="text-red-800 font-medium text-lg" data-id="element-155">
            The pain disrupts your sleep → Poor sleep lowers your pain threshold
            → Lower pain threshold makes you more sensitive to pain → Increased
            sensitivity disrupts sleep even more
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-6 mb-6" data-id="element-156">
          <div className="md:w-1/2" data-id="element-157">
            <video src="https://cdn.shopify.com/videos/c/o/v/69b41b221ce845b7babd880f446a8c27.mp4" autoPlay loop muted playsInline className="rounded-lg w-full h-56 object-cover" data-id="element-158" />
          </div>
          <div className="md:w-1/2" data-id="element-159">
            <p className="mb-4 text-gray-700 text-lg" data-id="element-160">
              This cycle explains why so many people tell me: "I can't remember
              the last time I woke up feeling refreshed" or "I'm more tired when
              I wake up than when I went to bed."
            </p>
            <p className="mb-4 text-gray-700 text-lg" data-id="element-161">
              Your body is fighting this misalignment battle all night long
              instead of healing and recovering like it's supposed to.
            </p>
            <p className="font-medium text-gray-700 text-lg" data-id="element-162">
              And if you're over 40, this gets exponentially worse.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-green-50 p-6 border border-green-100 rounded-lg" data-id="element-163">
        <h2 className="text-3xl font-bold text-green-800 mb-4" data-id="element-164">
          Why that shooting pain might be the best news you'll hear all day
        </h2>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-165">
          Dr. Mitchell explained something that completely changed my
          perspective:
        </p>
        <div className="bg-white p-4 rounded-lg border border-green-200 mb-4 italic" data-id="element-166">
          <p className="text-gray-700 text-lg" data-id="element-167">
            "When people come to me complaining about sharp, shooting pain or
            burning sensations, I actually get excited. You know why? Because
            pain means your nerves are still alive and fighting."
          </p>
        </div>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-168">Here's what she meant:</p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-169">
          As the Nocturnal Misalignment Cascade continues night after night,
          your compressed nerves go through stages. First, they send sharp pain
          signals as a warning. Then, as compression worsens, you feel burning
          or tingling. Finally, if the damage becomes severe enough, they simply
          go numb.
        </p>
        <p className="mb-4 text-gray-700 font-bold text-lg" data-id="element-170">
          Numbness is when you know the damage may be permanent.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-171">
          But if you're still feeling pain – even excruciating pain – it means
          your nerves are still functional and sending signals. They're still
          alive.
        </p>
        <div className="bg-white p-4 rounded-lg border border-green-200 mb-4" data-id="element-172">
          <p className="text-gray-700 italic text-lg" data-id="element-173">
            According to the American Academy of Orthopedic Surgeons:
            "Mechanical compression issues that are addressed before permanent
            nerve damage occurs often respond well to proper biomechanical
            intervention."
          </p>
        </div>
        <p className="mb-4 text-gray-700 font-bold text-green-800 text-xl" data-id="element-174">
          Translation: It's not too late. Your pain might actually be your
          body's way of telling you there's still time to fix this.
        </p>
      </div>
    </section>;
};